import { Component, ElementRef, Inject, inject, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { AdvisorBarLayoutService, AuthService, DynamicSidenav, DynamicSidenavService, EnvironmentService, HeaderService, ISidenavItemProvider, Q8Breakpoints } from 'core';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreakpointObserver } from '@angular/cdk/layout';
import { filter, map, shareReplay } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { AsyncPipe, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { SidenavItem } from 'design-system';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { CommonModule } from '@angular/common';
import { APP_VERSION } from '../../app.constants';

@Component({
  selector: 'app-base-layout',
  imports: [
    RouterOutlet, RouterModule, MatSidenavModule, 
    MatListModule, MatButtonModule,
    MatToolbarModule, MatIconModule, MatInputModule,
    AsyncPipe, MatMenuModule,
    MatFormFieldModule, NgComponentOutlet,
    CdkPortalOutlet, FormsModule, CommonModule, 
    TranslocoDirective, SidenavItem, MatTooltipModule,
  ],
  templateUrl: './base-layout.html',
  styleUrl: './base-layout.scss'
})
export class BaseLayoutComponent {
  protected readonly baseUrl = `${(inject(EnvironmentService).baseApiUrl() || window.location.origin).replace(/\/+$/, '')}/`;
  protected readonly appVersion = APP_VERSION;

  // Service per gestire l'advisor bar
  public advisorBarLayoutService: AdvisorBarLayoutService = inject(AdvisorBarLayoutService);

  // Gestione stato interno
  private _isAdvisorOpen = new BehaviorSubject<boolean>(true);
  isAdvisorOpen = this._isAdvisorOpen.asObservable();

  private transloco = inject(TranslocoService);
  private locale = inject(TranslocoLocaleService);
  languages = ['it-IT', 'en-GB', 'en-US', "fr-FR"];
  currentLang = this.transloco.getActiveLang();

  // sidenav
  menuList: DynamicSidenav[] = [];
  @ViewChild('sidenav') sidenav: any;

  // Pulsante incassi hardcodato
  // revenuesSidenavItem: DynamicSidenav = {
  //   id: 'revenues-static-link',
  //   path: '/revenues',
  //   label: 'Incassi hardcoded',
  //   icon: 'fa-person-digging',
  //   iconVariant: 'regular'
  // };

  // Pulsante profilo hardcodato
  profileSidenavItem: DynamicSidenav = {
    id: 'profile-static-link',
    path: '/profile',
    label: 'Profilo',
    icon: 'fa-user',
    iconVariant: 'regular'
  };

  isDesktop!: Observable<boolean>;
  isMobile!: Observable<boolean>;

  // Lista di providers per gli item della sidenav
  sidenavItemProviders: ISidenavItemProvider[];

  //fix temporaneo per reimpostare lo scroll in cima alla pagina durante navigazione
  private router = inject(Router);
  @ViewChild('content', { read: ElementRef })
  private contentEl!: ElementRef<HTMLElement>;
  private headerService = inject(HeaderService);

  //Espongo il signal readonly direttamente perchè getTitle() è statico e
  //ritornerebbe una stringa vuota dato che baseLayout viene caricato prima  
  //del componente effettivo mostrato nella pagina
  headerTitle = this.headerService.title;

  headerActions = this.headerService.actions;

  ngAfterViewInit(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.contentEl.nativeElement.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
  }

  constructor(
    @Inject(ISidenavItemProvider.getToken())
    sidenavItemProvider: ISidenavItemProvider[],
    private sidenavService: DynamicSidenavService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.sidenavItemProviders = sidenavItemProvider;
  }

  ngOnInit(): void {
    this.advisorBarLayoutService.advisorPortal.subscribe(portal => {
      if (portal) this._isAdvisorOpen.next(false);
    });

    this.reloadSidenav();

    this.isDesktop = this.breakpointObserver
      .observe([`(min-width: ${Q8Breakpoints.desktop}px)`])
      .pipe(
        map(state => state.matches),
        shareReplay(1)
      );

    this.isMobile = this.breakpointObserver
      .observe([`(max-width: ${Q8Breakpoints.tablet}px)`])
      .pipe(
        map(state => state.matches),
        shareReplay(1)
      );
  }

  toggleAdvisorBar() {
    this._isAdvisorOpen.next(!this._isAdvisorOpen.value);
  }

  onToggleSidenav() {
    this.sidenav.toggle();
  }

  //per chiudere la sidenav in mobile/tablet quando clicck su una voce di menu
  onCloseSidenav() {
    if (!this.breakpointObserver.isMatched('(min-width: 1200px)')) {
      this.sidenav.close();
    }
  }

  handleClose = () => this.onCloseSidenav();

  getRegularIconClasses(iconKey: string): string {
    if (!iconKey) {
      return 'fa-regular';
    }

    if (iconKey.startsWith('fa-')) {
      return `fa-regular ${iconKey}`;
    }

    const normalizedIcon = iconKey
      .replace(/^fa/, '')
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .toLowerCase();

    return `fa-regular fa-${normalizedIcon}`;
  }

  // Cerco nella lista di provider quello che ha il describe uguale al [providerName].
  // Se lo trovo ritorno il componente (con il .evaluate) in alternativa uso il default.
  getSidenavProviderComponent(providerName: string) {
    const provider = this.sidenavItemProviders.find(p => p.describe().providerName === providerName);
    
    // Se non trova il provider usa il default
    if(!provider) {
      const defaultProvider = this.sidenavItemProviders.find(p => p.describe().providerName === '');
      return defaultProvider?.evaluate();
    }
    return provider?.evaluate();
  }

  // Versione precedente per il cambio lingua senza aggiornamento della sidenav
  // setLang(lang: string) {
  //   if (!this.languages.includes(lang)) return;

  //   this.currentLang = lang;
  //   this.transloco.setActiveLang(lang);
  //   this.locale.setLocale(lang);
  // }

  // Metodo per il quale al cambio lingua ricarico la sidenav
  private reloadSidenav(lang?: string) {
    const active = lang ?? this.transloco.getActiveLang();

    this.sidenavService.loadSidenav().subscribe({
      next: (data) => {
        this.menuList = data;
      },
      error: (err) => {
        console.error("Errore durante il ricaricamento della sidenav", err);
        this.menuList = [];
      }
    });
  }

  async setLang(lang: string) {
    if (!this.languages.includes(lang)) return;
    
    this.currentLang = lang;
    this.transloco.setActiveLang(lang);
    this.locale.setLocale(lang);
    
    this.reloadSidenav(lang);
  }
}

